import { useAuth } from "@clerk/react";
import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";

import { env } from "@/web/lib/env";

const clerkTokenQueryKey = (sessionId: string) => ["clerk-token", sessionId] as const;

const tokenQueryOptions = (
  sessionId: string,
  getToken: (options?: { template?: string }) => Promise<string | null>,
) =>
  queryOptions({
    queryKey: clerkTokenQueryKey(sessionId),
    queryFn: async () => {
      const token = await getToken({
        template: env.VITE_CLERK_SPACETIMEDB_JWT_TEMPLATE,
      });

      if (!token) {
        throw new Error(
          "Clerk returned no token. Configure VITE_CLERK_SPACETIMEDB_JWT_TEMPLATE and a matching Clerk JWT template.",
        );
      }

      return token;
    },
    staleTime: 60_000,
    retry: false,
  });

async function delay(ms: number) {
  await new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Suspense hook for fetching Clerk token. Use only in App.tsx for the SpacetimeDB connection
 * builder.
 */
export function useClerkToken(): string | null {
  const { isLoaded, isSignedIn, sessionId, getToken } = useAuth();
  const queryClient = useQueryClient();
  const activeSessionId = sessionId ?? "__no-active-session__";
  const tokenQuery = useQuery({
    ...tokenQueryOptions(activeSessionId, getToken),
    enabled: isLoaded && isSignedIn === true && Boolean(sessionId),
  });

  if (!isLoaded) {
    throw delay(50);
  }

  if (!isSignedIn) {
    return null;
  }

  // Clerk can report signed-in before the active session id is ready.
  if (!sessionId) {
    throw delay(50);
  }

  if (tokenQuery.error) {
    throw tokenQuery.error;
  }

  if (tokenQuery.data) {
    return tokenQuery.data;
  }

  throw queryClient.ensureQueryData(tokenQueryOptions(sessionId, getToken));
}
